import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import './Admin.css';
import logoImg from '../assets/logo.jpeg';

export default function AdminPanel({ navigateTo }) {
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!sessionStorage.getItem('ndtools_token'));
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // ── Data state ──
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [brochures, setBrochures] = useState([]);


  // ── Modal state ──
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState('');

  // ── Load data ──
  const refreshData = useCallback(async () => {
    try {
      const [prodRes, catRes, galRes, broRes] = await Promise.all([
        api.getProducts(),
        api.getCategories(),
        api.getGallery(),
        api.getBrochures()
      ]);
      setProducts(prodRes);
      setCategories(catRes);
      setGallery(galRes);
      setBrochures(broRes);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  }, []);

  useEffect(() => { if (isLoggedIn) refreshData(); }, [isLoggedIn, refreshData]);

  // ── Toast helper ──
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // ── Login ──
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const data = await api.login(loginUser, loginPass);
      sessionStorage.setItem('ndtools_token', data.token);
      setIsLoggedIn(true);
      setLoginError('');
    } catch (err) {
      setLoginError('Invalid credentials');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('ndtools_token');
    setIsLoggedIn(false);
  };

  // ──────── LOGIN SCREEN ────────
  if (!isLoggedIn) {
    return (
      <div className="admin-login-page">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img src={logoImg} alt="N.D.Tools" className="admin-login-logo" style={{ height: '80px', marginBottom: '1.5rem' }} />
            <h1>N.D.Tools</h1>
            <p>Admin Panel</p>
          </div>
          <form onSubmit={handleLogin} className="admin-login-form">
            {loginError && <div className="admin-alert admin-alert-error">{loginError}</div>}
            <div className="admin-field">
              <label>Username</label>
              <input type="text" value={loginUser} onChange={e => setLoginUser(e.target.value)} placeholder="admin@ndtools.com" autoFocus />
            </div>
            <div className="admin-field">
              <label>Password</label>
              <input type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder="Enter password" />
            </div>
            <button type="submit" className="admin-btn admin-btn-primary admin-btn-full">Sign In</button>
          </form>
          <button className="admin-back-link" onClick={() => navigateTo('home')}>← Back to Website</button>
        </div>
      </div>
    );
  }

  // ──────── TABS ────────
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'products', label: 'Products', icon: '🔧' },
    { id: 'categories', label: 'Categories', icon: '📁' },
    { id: 'gallery', label: 'Gallery', icon: '🖼️' },
    { id: 'brochures', label: 'e-Brochures', icon: '📄' },
  ];

  // ──────── HELPERS ────────
  const openModal = (type, item = null) => { setModalType(type); setEditItem(item); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setEditItem(null); setModalType(''); };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c._id === catId || c.id === catId);
    return cat ? cat.name : catId;
  };

  // ──────── PRODUCT ACTIONS ────────
  const handleSaveProduct = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        categoryId: parseInt(formData.categoryId),
        description: formData.desc || 'No description',
        images: formData.img ? [{ url: formData.img }] : [],
        technicalSpecs: formData.technicalSpecs || [],
        keySpecs: formData.keySpecs || []
      };

      if (modalType === 'editProduct' && editItem) {
        // Backend update logic would go here
        await api.updateProduct(editItem._id, payload);
        showToast('Product updated!');
      } else {
        await api.addProduct(payload);
        showToast('Product added!');
      }
      refreshData();
      closeModal();
    } catch (err) {
      alert("Error saving product");
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await api.deleteProduct(id);
        refreshData();
        showToast('Product deleted');
      } catch (err) {
        alert("Backend doesn't support delete yet or error occurred.");
      }
    }
  };

  // ──────── CATEGORY ACTIONS ────────
  const handleSaveCategory = async (formData) => {
    try {
      if (editItem) {
        await api.updateCategory(editItem._id, { name: formData.name, description: formData.desc, img: formData.img });
        showToast('Category updated!');
      } else {
        await api.addCategory({ name: formData.name, description: formData.desc, img: formData.img });
        showToast('Category added!');
      }
      refreshData();
      closeModal();
    } catch (err) {
      alert("Error saving category");
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Delete this category?')) {
      try {
        await api.deleteCategory(id);
        refreshData();
        showToast('Category deleted');
      } catch (err) {
        alert("Backend doesn't support delete yet or error occurred.");
      }
    }
  };

  // ──────── GALLERY ACTIONS ────────
  const handleSaveGalleryItem = async (data) => {
    try {
      if (modalType === 'editGallery' && editItem) {
        await api.updateGallery(editItem._id, data);
        showToast('Gallery item updated!');
      } else {
        await api.addGallery(data);
        showToast('Gallery item added!');
      }
      refreshData();
      closeModal();
    } catch (err) {
      alert("Error saving gallery item");
    }
  };

  const handleDeleteGalleryItem = async (id) => {
    if (window.confirm('Delete this gallery item?')) {
      try {
        await api.deleteGallery(id);
        refreshData();
        showToast('Gallery item removed');
      } catch (err) {
        alert("Error deleting gallery item");
      }
    }
  };

  // ──────── BROCHURE ACTIONS ────────
  const handleSaveBrochure = async (brochureData) => {
    try {
      const data = new FormData();
      data.append('title', brochureData.title);
      data.append('description', brochureData.description || '');
      data.append('thumbnailUrl', brochureData.thumbnailUrl || '');
      data.append('category', 'Brochures');
      
      if (brochureData.pdfFile) {
        data.append('pdf', brochureData.pdfFile);
      } else {
        data.append('fileUrl', brochureData.fileUrl);
      }

      if (modalType === 'editBrochure' && editItem) {
        await api.updateBrochure(editItem._id, data);
        showToast('Brochure updated!');
      } else {
        await api.addBrochure(data);
        showToast('Brochure added!');
      }
      refreshData();
      closeModal();
    } catch (err) {
      alert("Error saving brochure");
    }
  };

  const handleDeleteBrochure = async (id) => {
    if (window.confirm('Delete this brochure?')) {
      try {
        await api.deleteBrochure(id);
        refreshData();
        showToast('Brochure deleted');
      } catch (err) {
        alert("Error deleting brochure");
      }
    }
  };

  // ──────── RENDER ────────
  return (
    <div className={`admin-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {toast && <div className="admin-toast">{toast}</div>}
      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''} ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="admin-sidebar-brand">
          <img src={logoImg} alt="Logo" style={{ height: '40px', width: 'auto' }} />
          <div>
            <h2>N.D.Tools</h2>
            <span className="admin-brand-sub">Control Panel</span>
          </div>
          <button
            className="admin-collapse-btn"
            onClick={() => setSidebarCollapsed(c => !c)}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '▶' : '◀'}
          </button>
        </div>

        <nav className="admin-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              title={sidebarCollapsed ? tab.label : ''}
            >
              <span className="admin-nav-icon">{tab.icon}</span>
              <span className="admin-nav-label">{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="admin-nav-item logout" onClick={handleLogout} title={sidebarCollapsed ? 'Logout' : ''}>
            <span className="admin-nav-icon">🚪</span>
            <span className="admin-nav-label">Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <span /><span /><span />
          </button>
          <h1 className="admin-page-title">{tabs.find(t => t.id === activeTab)?.label}</h1>
          <div className="admin-topbar-actions" style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <button className="admin-btn admin-btn-outline" onClick={() => navigateTo('home')}>
              🌐 View Site
            </button>
            <div className="admin-user-chip">
              <div className="admin-avatar">A</div>
            </div>
          </div>
        </header>

        <div className="admin-content">
          {activeTab === 'dashboard' && (
            <DashboardTab
              products={products}
              categories={categories}
              gallery={gallery}
              brochures={brochures}
              openModal={openModal}
              setActiveTab={setActiveTab}
            />
          )}
          {activeTab === 'products' && (
            <ProductsTab products={products} categories={categories} openModal={openModal} onDelete={handleDeleteProduct} getCategoryName={getCategoryName} />
          )}
          {activeTab === 'categories' && (
            <CategoriesTab categories={categories} products={products} openModal={openModal} onDelete={handleDeleteCategory} />
          )}
          {activeTab === 'gallery' && (
            <GalleryTab gallery={gallery} openModal={openModal} onDelete={handleDeleteGalleryItem} />
          )}
          {activeTab === 'brochures' && (
            <BrochuresTab brochures={brochures} openModal={openModal} onDelete={handleDeleteBrochure} />
          )}
        </div>
      </main>

      {showModal && (
        <ModalOverlay onClose={closeModal}>
          {(modalType === 'addProduct' || modalType === 'editProduct') && (
            <ProductForm categories={categories} initial={editItem} onSave={handleSaveProduct} onCancel={closeModal} isEdit={modalType === 'editProduct'} />
          )}
          {(modalType === 'addCategory' || modalType === 'editCategory') && (
            <CategoryForm initial={editItem} onSave={handleSaveCategory} onCancel={closeModal} isEdit={modalType === 'editCategory'} />
          )}
          {(modalType === 'addGallery' || modalType === 'editGallery') && (
            <GalleryForm initial={editItem} onSave={handleSaveGalleryItem} onCancel={closeModal} isEdit={modalType === 'editGallery'} />
          )}
          {(modalType === 'addBrochure' || modalType === 'editBrochure') && (
            <BrochureForm initial={editItem} onSave={handleSaveBrochure} onCancel={closeModal} isEdit={modalType === 'editBrochure'} />
          )}
        </ModalOverlay>
      )}
    </div>
  );
}

// SUB-COMPONENTS

function useCountUp(target, duration = 700) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setCount(start);
      if (start >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

function StatCard({ icon, value, label, onClick }) {
  const displayed = useCountUp(value);
  return (
    <div className="admin-stat-card clickable" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="admin-stat-icon">{icon}</div>
      <div className="admin-stat-info">
        <span className="admin-stat-number">{displayed}</span>
        <span className="admin-stat-label">{label}</span>
      </div>
    </div>
  );
}

function DashboardTab({ products, categories, gallery, brochures, openModal, setActiveTab }) {
  return (
    <>
      <div className="admin-stats-row">
        <StatCard icon="🔧" value={products.length}   label="Products"       onClick={() => setActiveTab('products')} />
        <StatCard icon="📁" value={categories.length} label="Categories"     onClick={() => setActiveTab('categories')} />
        <StatCard icon="🖼️" value={gallery.length}    label="Gallery Images" onClick={() => setActiveTab('gallery')} />
        <StatCard icon="📄" value={brochures.length}  label="e-Brochures"    onClick={() => setActiveTab('brochures')} />
      </div>
      <div className="admin-section-title">Quick Actions</div>
      <div className="admin-quick-actions">
        <button className="admin-btn admin-btn-primary"   onClick={() => openModal('addProduct')}>+ Add Product</button>
        <button className="admin-btn admin-btn-secondary" onClick={() => openModal('addCategory')}>+ Add Category</button>
        <button className="admin-btn admin-btn-secondary" onClick={() => openModal('addGallery')}>+ Add Gallery Image</button>
        <button className="admin-btn admin-btn-secondary" onClick={() => openModal('addBrochure')}>+ Add Brochure</button>
      </div>
    </>
  );
}

function ProductsTab({ products, categories, openModal, onDelete, getCategoryName }) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const filtered = products.filter(p => {
    const matchName = p.name?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat ? (p.category?._id || p.category) === filterCat : true;
    return matchName && matchCat;
  });

  return (
    <>
      <div className="admin-toolbar">
        <input type="text" className="admin-search" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="admin-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>)}
        </select>
        <button className="admin-btn admin-btn-primary" onClick={() => openModal('addProduct')}>+ Add Product</button>
      </div>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr><th>Image</th><th>Name</th><th>Category</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="4" className="admin-empty">No products found</td></tr>
            ) : filtered.map(p => {
              const imgUrl = p.images?.[0]?.url || p.img;
              return (
                <tr key={p._id}>
                  <td>
                    <img
                      src={imgUrl}
                      alt={p.name}
                      className="admin-table-thumb"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=No+Image'; }}
                    />
                  </td>
                  <td><strong>{p.name}</strong></td>
                  <td><span className="admin-badge">{p.category?.name || getCategoryName(p.category)}</span></td>
                  <td className="admin-actions-cell">
                    <button className="admin-btn-icon" title="Edit" onClick={() => openModal('editProduct', p)}>✏️</button>
                    <button className="admin-btn-icon danger" title="Delete" onClick={() => onDelete(p._id)}>🗑️</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CategoriesTab({ categories, products, openModal, onDelete }) {
  return (
    <>
      <div className="admin-toolbar">
        <div />
        <button className="admin-btn admin-btn-primary" onClick={() => openModal('addCategory')}>+ Add Category</button>
      </div>
      <div className="admin-cards-grid">
        {categories.map(cat => {
          return (
            <div key={cat._id} className="admin-category-card">
              <div className="admin-category-img-wrapper">
                <img 
                  src={cat.img} 
                  alt={cat.name} 
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/300x150?text=No+Image'; }}
                />
              </div>
              <div className="admin-category-body">
                <h3>{cat.name}</h3>
                <p>{cat.description}</p>
                <div className="admin-category-actions">
                  <button className="admin-btn admin-btn-sm admin-btn-outline" onClick={() => openModal('editCategory', cat)}>Edit</button>
                  <button className="admin-btn admin-btn-sm admin-btn-danger" onClick={() => onDelete(cat._id)}>Delete</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function GalleryTab({ gallery, openModal, onDelete }) {
  return (
    <>
      <div className="admin-toolbar">
        <div />
        <button className="admin-btn admin-btn-primary" onClick={() => openModal('addGallery')}>+ Add Media</button>
      </div>
      <div className="admin-gallery-grid">
        {gallery.length === 0 ? (
          <p className="admin-empty-block">No gallery items yet.</p>
        ) : gallery.map((item, idx) => (
          <div key={item._id || idx} className="admin-gallery-item">
            {item.type === 'video' ? (
              <div className="admin-gallery-video-preview">
                <img src={item.thumbnail} alt={item.title} />
                <span className="play-icon">▶</span>
              </div>
            ) : (
              <img src={item.url} alt="Gallery" />
            )}
            <div className="admin-gallery-actions-overlay">
              <button className="admin-btn-icon sm" title="Edit" onClick={() => openModal('editGallery', item)}>✏️</button>
              <button className="admin-btn-icon sm danger" title="Delete" onClick={() => onDelete(item._id)}>🗑️</button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}



function ModalOverlay({ children, onClose }) {
  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <button className="admin-modal-close" onClick={onClose}>✕</button>
        {children}
      </div>
    </div>
  );
}

const DEFAULT_SPECS = [
  { key: 'Modal No.', value: '' },
  { key: 'Rolls Dimension (Inch)', value: '' },
  { key: 'Rolls Dimension (mm)', value: '' },
  { key: 'Design Roll', value: '' },
  { key: 'Max Apertures', value: '' },
  { key: 'No. of Half Round Grooves', value: '' },
  { key: 'Machine Net Weight', value: '' },
  { key: 'Machine Dimension (Inch)', value: '' },
  { key: 'Shipping Dimension (cm)', value: '' },
  { key: 'Shipping Weight', value: '' }
];

function ProductForm({ categories, initial, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    categoryId: initial?.category?._id || (categories[0]?._id || ''),
    img: initial?.images?.[0]?.url || '',
    desc: initial?.description || '',
    technicalSpecs: initial?.technicalSpecs && initial.technicalSpecs.length > 0
      ? initial.technicalSpecs
      : (isEdit ? [] : DEFAULT_SPECS),
    keySpecs: initial?.keySpecs && initial.keySpecs.length > 0
      ? initial.keySpecs
      : []
  });

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSpecChange = (index, field, value) => {
    const updatedSpecs = [...form.technicalSpecs];
    updatedSpecs[index][field] = value;
    setForm(prev => ({ ...prev, technicalSpecs: updatedSpecs }));
  };

  const addSpecField = () => {
    setForm(prev => ({
      ...prev,
      technicalSpecs: [...prev.technicalSpecs, { key: '', value: '' }]
    }));
  };

  const handleKeySpecChange = (index, value) => {
    const updated = [...form.keySpecs];
    updated[index] = value;
    setForm(prev => ({ ...prev, keySpecs: updated }));
  };

  const addKeySpecField = () => {
    setForm(prev => ({
      ...prev,
      keySpecs: [...prev.keySpecs, '']
    }));
  };

  const removeKeySpecField = (index) => {
    const updated = form.keySpecs.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, keySpecs: updated }));
  };

  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = await api.uploadImage(file);
      handleChange('img', data.url);
      setUploading(false);
    } catch (err) {
      alert("Upload failed");
      setUploading(false);
    }
  };

  const removeSpecField = (index) => {
    const updatedSpecs = form.technicalSpecs.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, technicalSpecs: updatedSpecs }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert('Product name is required.');
    // Filter out empty specs
    const cleanedSpecs = form.technicalSpecs.filter(s => s.key.trim() && s.value.trim());
    const cleanedKeySpecs = form.keySpecs.filter(s => s.trim());
    onSave({ ...form, technicalSpecs: cleanedSpecs, keySpecs: cleanedKeySpecs });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-modal-form">
      <h2>{isEdit ? 'Edit Product' : 'Add Product'}</h2>
      <div className="admin-field">
        <label>Product Name *</label>
        <input value={form.name} onChange={e => handleChange('name', e.target.value)} autoFocus />
      </div>
      <div className="admin-field">
        <label>Category</label>
        <select value={form.categoryId} onChange={e => handleChange('categoryId', e.target.value)}>
          {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </div>
      <div className="admin-field">
        <label>Product Image</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Image URL"
            value={form.img}
            onChange={e => handleChange('img', e.target.value)}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>or</span>
          <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            {uploading ? 'Uploading...' : '📁 Choose File'}
            <input type="file" hidden onChange={handleFileChange} accept="image/*" />
          </label>
        </div>
        {form.img && (
          <div style={{ marginTop: '0.5rem' }}>
            <img
              src={form.img}
              alt="Preview"
              style={{ width: '60px', height: '60px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #ddd' }}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL'; }}
            />
          </div>
        )}
      </div>
      <div className="admin-field">
        <label>Description</label>
        <textarea rows="3" value={form.desc} onChange={e => handleChange('desc', e.target.value)} />
      </div>

      <div className="admin-section-divider"></div>
      <div className="admin-section-header">
        <h3>Key Specifications (Bullet Points)</h3>
        <p>Add up to 4 high-level features for this product (e.g. "Durable anti-corrosive coating"). If left blank, default generic features will be shown.</p>
      </div>

      <div className="admin-specs-container" style={{ marginBottom: '1.5rem' }}>
        {form.keySpecs.length === 0 && (
          <p className="admin-empty-text">No key specifications added yet (showing defaults).</p>
        )}
        {form.keySpecs.map((bullet, idx) => (
          <div key={idx} className="admin-spec-row">
            <input
              placeholder="Key Specification Bullet (e.g. Strict engineering tolerance benchmarks)"
              value={bullet}
              onChange={e => handleKeySpecChange(idx, e.target.value)}
              className="admin-spec-input"
              style={{ flex: 1 }}
            />
            <button type="button" className="admin-btn-icon danger" onClick={() => removeKeySpecField(idx)} title="Remove Key Spec">
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={addKeySpecField} style={{ marginTop: '0.5rem' }}>
          + Add New Key Specification
        </button>
      </div>

      <div className="admin-section-divider"></div>
      <div className="admin-section-header">
        <h3>Technical Specifications</h3>
        <p>Add key features and technical details for this product.</p>
      </div>

      <div className="admin-specs-container">
        {form.technicalSpecs.length === 0 && (
          <p className="admin-empty-text">No specifications added yet.</p>
        )}
        {form.technicalSpecs.map((spec, idx) => (
          <div key={idx} className="admin-spec-row">
            <input
              placeholder="Property (e.g. Weight)"
              value={spec.key}
              onChange={e => handleSpecChange(idx, 'key', e.target.value)}
              className="admin-spec-input"
            />
            <input
              placeholder="Value (e.g. 15 kg)"
              value={spec.value}
              onChange={e => handleSpecChange(idx, 'value', e.target.value)}
              className="admin-spec-input"
            />
            <button type="button" className="admin-btn-icon danger" onClick={() => removeSpecField(idx)} title="Remove Spec">
              ✕
            </button>
          </div>
        ))}
        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={addSpecField} style={{ marginTop: '0.5rem' }}>
          + Add New Specification
        </button>
      </div>

      <div className="admin-modal-actions">
        <button type="button" className="admin-btn admin-btn-outline" onClick={onCancel}>Cancel</button>
        <button type="submit" className="admin-btn admin-btn-primary">{isEdit ? 'Update' : 'Add Product'}</button>
      </div>
    </form>
  );
}

function CategoryForm({ initial, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    desc: initial?.description || '',
    img: initial?.img || ''
  });
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = await api.uploadImage(file);
      setForm(prev => ({ ...prev, img: data.url }));
      setUploading(false);
    } catch (err) {
      alert("Upload failed");
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return alert('Category name is required.');
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="admin-modal-form">
      <h2>{isEdit ? 'Edit Category' : 'Add Category'}</h2>
      <div className="admin-field">
        <label>Category Name *</label>
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
      </div>
      <div className="admin-field">
        <label>Category Image</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Image URL"
            value={form.img}
            onChange={e => setForm({ ...form, img: e.target.value })}
            style={{ flex: 1 }}
          />
          <span style={{ fontSize: '0.8rem', color: '#64748b' }}>or</span>
          <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            {uploading ? 'Uploading...' : '📁 Choose File'}
            <input type="file" hidden onChange={handleFileChange} accept="image/*" />
          </label>
        </div>
        {form.img && (
          <div style={{ marginTop: '0.5rem' }}>
            <img
              src={form.img}
              alt="Preview"
              style={{ width: '60px', height: '60px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #ddd' }}
              onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Invalid+URL'; }}
            />
          </div>
        )}
      </div>
      <div className="admin-field">
        <label>Description</label>
        <textarea rows="2" value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })} />
      </div>
      <div className="admin-modal-actions">
        <button type="button" className="admin-btn admin-btn-outline" onClick={onCancel}>Cancel</button>
        <button type="submit" className="admin-btn admin-btn-primary">{isEdit ? 'Update' : 'Add Category'}</button>
      </div>
    </form>
  );
}

function GalleryForm({ initial, onSave, onCancel, isEdit }) {
  const [type, setType] = useState(initial?.type || 'image'); 
  const [form, setForm] = useState({ 
    url: initial?.url || '', 
    title: initial?.title || '', 
    youtubeId: initial?.youtubeId || '', 
    thumbnail: initial?.thumbnail || '' 
  });
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploading(true);
      const data = await api.uploadImage(file);
      if (type === 'image') setForm({ ...form, url: data.url });
      else setForm({ ...form, thumbnail: data.url });
      setUploading(false);
    } catch (err) {
      alert("Upload failed");
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (type === 'image') {
      if (!form.url) return alert('Image is required');
      onSave({ type: 'image', url: form.url, title: form.title || 'Gallery Image' });
    } else {
      if (!form.youtubeId || !form.title) return alert('Title and YouTube ID are required');
      onSave({ type: 'video', youtubeId: form.youtubeId, title: form.title, thumbnail: form.thumbnail || `https://img.youtube.com/vi/${form.youtubeId}/0.jpg` });
    }
  };

  return (
    <div className="admin-modal-form">
      <h2>{isEdit ? 'Edit Gallery Item' : 'Add to Gallery'}</h2>
      
      {!isEdit && (
        <div className="admin-field">
          <label>Media Type</label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <input type="radio" checked={type === 'image'} onChange={() => setType('image')} /> Image
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
              <input type="radio" checked={type === 'video'} onChange={() => setType('video')} /> Video
            </label>
          </div>
        </div>
      )}

      <div className="admin-field">
        <label>Title</label>
        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Item Title" />
      </div>

      {type === 'image' ? (
        <div className="admin-field">
          <label>Select Image</label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <input placeholder="Image URL" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} style={{ flex: 1 }} />
            <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
              {uploading ? '...' : '📁'}
              <input type="file" hidden onChange={handleFileChange} accept="image/*" />
            </label>
          </div>
          {form.url && <img src={form.url} alt="Preview" style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', marginTop: '0.5rem', borderRadius: '8px', border: '1px solid #ddd' }} />}
        </div>
      ) : (
        <>
          <div className="admin-field">
            <label>YouTube Video ID *</label>
            <input value={form.youtubeId} onChange={e => setForm({ ...form, youtubeId: e.target.value })} placeholder="e.g. dQw4w9WgXcQ" />
          </div>
          <div className="admin-field">
            <label>Thumbnail Image</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input placeholder="Thumbnail URL" value={form.thumbnail} onChange={e => setForm({ ...form, thumbnail: e.target.value })} style={{ flex: 1 }} />
              <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                {uploading ? '...' : '📁'}
                <input type="file" hidden onChange={handleFileChange} accept="image/*" />
              </label>
            </div>
            {form.thumbnail && <img src={form.thumbnail} alt="Preview" style={{ width: '100%', maxHeight: '120px', objectFit: 'contain', marginTop: '0.5rem', borderRadius: '8px', border: '1px solid #ddd' }} />}
          </div>
        </>
      )}

      <div className="admin-modal-actions">
        <button type="button" className="admin-btn admin-btn-outline" onClick={onCancel}>Cancel</button>
        <button type="button" className="admin-btn admin-btn-primary" onClick={handleSubmit} disabled={uploading}>
          {isEdit ? 'Update' : 'Add to Gallery'}
        </button>
      </div>
    </div>
  );
}

function BrochuresTab({ brochures, openModal, onDelete }) {
  return (
    <>
      <div className="admin-toolbar">
        <button className="admin-btn admin-btn-primary" onClick={() => openModal('addBrochure')}>+ Add e-Brochure</button>
      </div>
      <div className="admin-table-wrapper">
        <table className="admin-table">
          <thead>
            <tr><th>Cover</th><th>Title</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {brochures.length === 0 ? (
              <tr><td colSpan="3" className="admin-empty">No brochures found</td></tr>
            ) : brochures.map(b => (
              <tr key={b._id}>
                <td>
                  <img
                    src={b.thumbnailUrl}
                    alt={b.title}
                    className="admin-table-thumb"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=PDF'; }}
                  />
                </td>
                <td>
                  <strong>{b.title}</strong>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{b.description}</div>
                </td>
                <td className="admin-actions-cell">
                  <a href={b.fileUrl} target="_blank" rel="noopener noreferrer" className="admin-btn-icon" title="View PDF">👁️</a>
                  <button className="admin-btn-icon" title="Edit" onClick={() => openModal('editBrochure', b)}>✏️</button>
                  <button className="admin-btn-icon danger" title="Delete" onClick={() => onDelete(b._id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BrochureForm({ initial, onSave, onCancel, isEdit }) {
  const [form, setForm] = useState({ 
    title: initial?.title || '', 
    desc: initial?.description || '', 
    cover: initial?.thumbnailUrl || '', 
    pdf: initial?.fileUrl || '' 
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (field === 'pdf') {
      setPdfFile(file);
      setForm(prev => ({ ...prev, pdf: file.name }));
      return;
    }

    try {
      setUploadingCover(true);
      const data = await api.uploadImage(file);
      setForm(prev => ({ ...prev, cover: data.url }));
      setUploadingCover(false);
    } catch (err) {
      alert("Upload failed");
      setUploadingCover(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.pdf) return alert('Title and PDF are required.');
    onSave({ 
      title: form.title, 
      description: form.desc, 
      thumbnailUrl: form.cover, 
      fileUrl: form.pdf,
      pdfFile: pdfFile
    });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-modal-form">
      <h2>{isEdit ? 'Edit e-Brochure' : 'Add e-Brochure'}</h2>
      <div className="admin-field">
        <label>Title *</label>
        <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} autoFocus />
      </div>

      <div className="admin-field">
        <label>Cover Image</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input placeholder="Image URL" value={form.cover} onChange={e => setForm({ ...form, cover: e.target.value })} style={{ flex: 1 }} />
          <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            {uploadingCover ? '...' : '📁'}
            <input type="file" hidden onChange={e => handleFileUpload(e, 'cover')} accept="image/*" />
          </label>
        </div>
      </div>

      <div className="admin-field">
        <label>PDF File *</label>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input placeholder="PDF URL" value={form.pdf} onChange={e => setForm({ ...form, pdf: e.target.value })} style={{ flex: 1 }} />
          <label className="admin-btn admin-btn-secondary admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
            📁 Upload PDF
            <input type="file" hidden onChange={e => handleFileUpload(e, 'pdf')} accept=".pdf" />
          </label>
        </div>
        {form.pdf && <div style={{ fontSize: '0.75rem', color: 'green', marginTop: '0.2rem' }}>✓ PDF Ready</div>}
      </div>

      <div className="admin-field">
        <label>Description</label>
        <textarea rows="2" value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })} />
      </div>

      <div className="admin-modal-actions">
        <button type="button" className="admin-btn admin-btn-outline" onClick={onCancel}>Cancel</button>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={uploadingCover}>
          {isEdit ? 'Update Brochure' : 'Add Brochure'}
        </button>
      </div>
    </form>
  );
}
